/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import CustomModal from "../../../../../../../components/Modal/CustomModal";
import {CustomTextField} from "../../../../../../../components/CustomTextField";
import CustomPhotoPickerMultiple from "../../../../../../../components/PhotoPicker/CustomPhotoPickerMultiple";
import {PHOTO_PICKER_MODEL} from "../../../../../../../components/PhotoPicker/SubComponent/Models/Models";
import {DeleteIconsOptions} from "../../../../../../../components/buttons/IconButtons";
import {FILE_TYPE} from "../../../../../../../Helpers/fileType";
import {fileSize} from "../../../../../../../constant/variable";
import {isNotEmpty} from "../../../../../../../Apis/functions";
import {GATE_LOG_NOTES_LENGTH} from "../Model/Model";

/**
 * Report of the discrepancies found at the gate when the movement is dispatched.
 *
 * What the guard found is the reason the movement is closed with, so the report
 * asks for the comment that describes it and the evidence that supports it, and
 * neither of them is optional. The evidence is captured with the camera of the
 * gate, so the picker is displayed inside the modal and the report is hidden
 * while the camera is active.
 *
 * @param {boolean} show - Whether the report is displayed.
 * @param {Function} onHide - Closes the report.
 * @param {Function} onConfirm - Registers the report with the comment and the evidence.
 * @param {string} [title_modal] - Translation key of the title of the report.
 * @param {string} [description] - Description displayed in the body of the report.
 * @param {string} [subDescription] - Instruction displayed above the fields of the report.
 *
 * @returns {JSX.Element} Report of the discrepancies of the movement.
 */
const ReportDispatchModal = ({
                                 show,
                                 onHide,
                                 onConfirm,
                                 title_modal = 'report_dispatch',
                                 description,
                                 subDescription
                             }) => {
    const {t} = useTranslation();
    const [comment, setComment] = useState('');
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL});
    const [emptyFields, setEmptyFields] = useState(false);
    const [displayModal, setDisplayModal] = useState('');

    const emptyComment = !isNotEmpty(comment);
    const emptyEvidence = photoPicker?.listFiles?.length === 0;

    /**
     * Registers the report with what the guard captured.
     *
     * The comment and the evidence travel together with the movement, so the
     * guard is warned about what is left blank before the report is sent.
     *
     * @returns {void}
     */
    const onClickFn = () => {
        if (emptyComment || emptyEvidence) {
            setEmptyFields(true);
            return;
        }

        onConfirm(comment, photoPicker?.listFiles);
        setComment('');
        setPhotoPicker({...PHOTO_PICKER_MODEL});
        setEmptyFields(false);
        onHide();
    };

    return (
        <CustomModal
            show={show}
            title={t(title_modal)}
            size='md'
            buttonConfirm='confirm'
            display={displayModal}
            onHide={onHide}
            onConfirm={onClickFn}
        >
            <div className='col-12 text-center'>
                <p className='fs-14px'>{description}</p>
                <p className='text-uppercase fs-14px ibl-font-w-700 mt-3'>
                    {subDescription}
                </p>
            </div>
            <div className='col-12 mt-4'>
                <CustomTextField
                    length={GATE_LOG_NOTES_LENGTH}
                    id='txt_rdm_comment'
                    name='comment'
                    value={comment}
                    trans='comment'
                    emptyFields={emptyFields}
                    required={true}
                    onChange={(e) => setComment(e.target.value)}
                    multiline={true}
                    rows={4}
                />
            </div>
            <div className='col-12 mt-4'>
                <p className='font-color-general-950 fs-14px ibl-font-w-600 mb-3 text-uppercase text-center'>
                    {t('attach_evidence_required')} *
                </p>
                <CustomPhotoPickerMultiple
                    id={'fu_rdm_file'}
                    photoPickerFile={photoPicker}
                    setPhotoPickerFile={setPhotoPicker}
                    accept={{...FILE_TYPE.IMAGE_FILES, size: fileSize}}
                    titleInput={'take_photography'}
                    showBtnCamera={true}
                    // The discrepancy is reported with what the guard sees at
                    // the gate, so its evidence is taken with the camera and no
                    // file of the device is attached
                    showBtnUpload={false}
                    showBtnDeleteItemGallery={true}
                    deleteIcon={DeleteIconsOptions}
                    apiDelete={false}
                    inModal={true}
                    setDisplayModal={setDisplayModal}
                    required={true}
                    emptyFields={emptyFields && emptyEvidence}
                />
            </div>
        </CustomModal>
    );
};

export default ReportDispatchModal;
